/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState, useCallback } from 'react';
import { 
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  Grid,
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Stack,
  alpha,
  Alert,
  AlertTitle
} from '@mui/material';
import {  AutoCompleteProdutoIdProduto, BasicSelect,  IVFormErrors,  
  VFieldMoneynumber2nota, 
  VForm, 
  VTextField, 
  useVForm 
} from '../../../shared/forms';
import * as yup from 'yup';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import InventoryIcon from '@mui/icons-material/Inventory';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import WarningIcon from '@mui/icons-material/Warning';
import SyncAltIcon from '@mui/icons-material/SyncAlt';

interface IFormData {
  iditens: string;
  idprodutos: string;
  qtds: number;
  qtde: number;
  un: string;
  preco: number;
  ean?: string;
  ncm?: string;
  subtotal: number;
  desconto: number;
  voutros: number;
  total: number;
  filtro: string;
  referencia?: string;
  marca?: string;
  descricao: string;
}

export interface ConfirmationDialogRawProps {
  id: string;
  value: string;
  open: boolean;
  status: boolean;
  dados?: IFormData;
  onDados: (dados: IFormData) => void;
  onClose: (value?: string) => void;
}

// Atualizar schema de validação para tornar UN obrigatório
const formValidationSchema: yup.ObjectSchema<Omit<IFormData, 'iditens' | 'idprodutos' | 'filtro' | 'referencia' | 'marca'| 'qtds' | 'ncm'| 'total'| 'desconto'| 'voutros'>> = yup.object().shape({
  qtde: yup.number().required().min(0.0001, 'Quantidade deve ser maior que zero').default(0),
  preco: yup.number().required().moreThan(0, 'Preço deve ser maior que zero'),
  subtotal: yup.number().required(),
  un: yup.string().required('Unidade é obrigatória').min(1, 'Selecione uma unidade'),
  ean: yup.string().optional().default(''),
  descricao: yup.string().optional().default(''),
});

export const DialogNfeEntrada: React.FC<ConfirmationDialogRawProps> = (props: ConfirmationDialogRawProps) => {
  const { formRef, save } = useVForm();
  const { onClose, value: valueProp, open, ...other } = props;
  const [value, setValue] = useState(valueProp);
  const [isLoading, setIsLoading] = useState(false);
  const [id, setId] = useState('');
  const [idprodd, setIdprodd] = useState('');
  const [filtro, setfiltro] = useState('');
  const [descricao, setdescricao] = useState<string | undefined>('');
  const [referencia, setreferencia] = useState<string | undefined>('');
  const [marca, setmarca] = useState<string | undefined>('');
  const [un, setun] = useState<string | undefined>('');
  
  // Estados locais para monitorar quantidade e preço
  const [quantidade, setQuantidade] = useState<number>(0);
  const [precoUnitario, setPrecoUnitario] = useState<number>(0);
  const [totalCalculado, setTotalCalculado] = useState<number>(0);
  
  // Estado para armazenar o total original da nota
  const [totalNotaOriginal, setTotalNotaOriginal] = useState<number>(0);
  
  // Estado para controlar o erro de validação
  const [erroValidacao, setErroValidacao] = useState<string>('');
  const [erroUnidade, setErroUnidade] = useState<string>('');
  
  // Estado para armazenar a quantidade de casas decimais
  const [casasDecimais, setCasasDecimais] = useState<number>(4);
  
  // Estado para armazenar o valor unitário interno (com alta precisão)
  const [precoUnitarioInterno, setPrecoUnitarioInterno] = useState<number>(0);

  const itensnovo = {
    filtro: 'descricao',
    idprodutos: '',
    vuncom: 0,
    vprod: 0,
    referencia: '',
    marca: '',
    un: '',
  };

  // Função para detectar o número de casas decimais de um número
  const detectarCasasDecimais = (value: number | string | null | undefined): number => {
    if (value === null || value === undefined || value === '') return 2;
    
    let str: string;
    if (typeof value === 'number') {
      str = value.toString();
    } else {
      str = value;
    }
    
    // Verificar se tem ponto ou vírgula
    const match = str.match(/(?:[.,](\d+))/);
    if (match && match[1]) {
      const casas = match[1].length;
      // Se tiver 4 casas, usa 4, senão usa 2
      return casas === 4 ? 4 : 2;
    }
    
    return 2; // Padrão para valores monetários
  };

  // Função para formatar número para exibição
  const formatarParaExibicao = (value: number, casas: number): number => {
    if (casas === 4) {
      return Math.round(value * 10000) / 10000;
    } else {
      return Math.round(value * 100) / 100;
    }
  };

  // Função para formatar para exibição
  const formatMoneyBR = (value: number | string | null | undefined, casas?: number): string => {
    if (value === null || value === undefined || value === '') {
      return '0,00';
    }
    let numberValue: number;
    if (typeof value === 'string') {
      const clean = value.replace(/\./g, '').replace(',', '.');
      numberValue = Number(clean);
    } else {
      numberValue = value;
    }
    if (isNaN(numberValue)) {
      return '0,00';
    }
    
    const numeroCasas = casas !== undefined ? casas : casasDecimais;
    
    return numberValue.toLocaleString('pt-BR', {
      minimumFractionDigits: numeroCasas,
      maximumFractionDigits: numeroCasas,
    });
  };

  // Função para calcular o total com precisão total (sem arredondar)
  const calcularTotalExato = useCallback((quantidade: number, preco: number): number => {
    return quantidade * preco;
  }, []);

  // Função para calcular o preço unitário exato baseado na quantidade e total da nota
  const calcularPrecoExato = useCallback((quantidade: number, totalNota: number): number => {
    if (quantidade === 0) return 0;
    return totalNota / quantidade;
  }, []);

  // Função para calcular a quantidade exata baseada no preço unitário e total da nota
  const calcularQuantidadeExata = useCallback((preco: number, totalNota: number): number => {
    if (preco === 0) return 0;
    return totalNota / preco;
  }, []);

  // Função para validar se o total calculado é igual ao total da nota com tolerância
  const validarTotal = useCallback((totalCalculadoValue: number, totalNotaValue: number): boolean => {
    // Usar tolerância de 0.01 para 2 casas ou 0.0001 para 4 casas
    const tolerancia = casasDecimais === 4 ? 0.0001 : 0.0099;
    const diferenca = Math.abs(totalCalculadoValue - totalNotaValue);
    const isValid = diferenca <= tolerancia;
    
    if (!isValid && totalNotaValue > 0) {
      const totalExibido = totalCalculadoValue.toFixed(casasDecimais);
      const totalEsperado = totalNotaValue.toFixed(casasDecimais);
      setErroValidacao(
        `Os valores estão com diferença de R$ ${Math.abs(totalCalculadoValue - totalNotaValue).toFixed(4)}. ` +
        `A multiplicação (${quantidade} × ${precoUnitario.toFixed(casasDecimais)}) resultou em R$ ${totalExibido}, ` +
        `mas o total da nota é R$ ${totalEsperado}. ` +
        `Use o botão "Ajustar" para corrigir automaticamente.`
      );
    } else {
      setErroValidacao('');
    }
    
    return isValid;
  }, [casasDecimais, quantidade, precoUnitario]);

  // Função para ajustar o preço baseado na quantidade para bater o total
  const ajustarPrecoPelaQuantidade = useCallback(() => {
    if (totalNotaOriginal > 0 && quantidade > 0) {
      const novoPrecoExato = calcularPrecoExato(quantidade, totalNotaOriginal);
      const novoPrecoExibicao = formatarParaExibicao(novoPrecoExato, casasDecimais);
      
      setPrecoUnitarioInterno(novoPrecoExato);
      setPrecoUnitario(novoPrecoExibicao);
      setTotalCalculado(totalNotaOriginal);
      
      formRef.current?.setFieldValue('preco', novoPrecoExibicao);
      formRef.current?.setFieldValue('total', totalNotaOriginal);
      
      setErroValidacao('');
    }
  }, [totalNotaOriginal, quantidade, casasDecimais, calcularPrecoExato, formRef]);
  const ajustarQuantidadePeloPreco = useCallback(() => {
    if (totalNotaOriginal > 0 && precoUnitario > 0) {
      const novaQuantidadeExata = calcularQuantidadeExata(precoUnitarioInterno, totalNotaOriginal);
      const novaQuantidadeExibicao = formatarParaExibicao(novaQuantidadeExata, casasDecimais);
      
      setQuantidade(novaQuantidadeExibicao);
      setTotalCalculado(totalNotaOriginal);
      
      formRef.current?.setFieldValue('qtde', novaQuantidadeExibicao);
      formRef.current?.setFieldValue('total', totalNotaOriginal);
      
      setErroValidacao('');
    }
  }, [totalNotaOriginal, precoUnitario, precoUnitarioInterno, casasDecimais, calcularQuantidadeExata, formRef]);

  // Handler para mudança na quantidade
  const handleQuantidadeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const cleanValue = value.replace(/\./g, '').replace(',', '.');
    const novaQuantidade = parseFloat(cleanValue) || 0;
    
    setQuantidade(novaQuantidade);
    
    // Se temos um total da nota definido, calcular o preço unitário exato
    if (totalNotaOriginal > 0 && novaQuantidade > 0) {
      const novoPrecoExato = calcularPrecoExato(novaQuantidade, totalNotaOriginal);
      const novoPrecoExibicao = formatarParaExibicao(novoPrecoExato, casasDecimais);
      
      setPrecoUnitarioInterno(novoPrecoExato);
      setPrecoUnitario(novoPrecoExibicao);
      
      // Atualizar o campo de preço no formulário
      formRef.current?.setFieldValue('preco', novoPrecoExibicao);
      
      // O total calculado sempre será igual ao total da nota
      setTotalCalculado(totalNotaOriginal);
      formRef.current?.setFieldValue('subtotal', totalNotaOriginal);
      
      // Limpar erro de validação
      setErroValidacao('');
    } else {
      // Para novo item, recalcular total normalmente
      const novoTotal = calcularTotalExato(novaQuantidade, precoUnitarioInterno);
      const novoTotalFormatado = formatarParaExibicao(novoTotal, casasDecimais);
      setTotalCalculado(novoTotalFormatado);
      formRef.current?.setFieldValue('subtotal', novoTotalFormatado);
      
      if (totalNotaOriginal > 0) {
        validarTotal(novoTotalFormatado, totalNotaOriginal);
      }
    }
  };

  // Handler para mudança no preço
  const handlePrecoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const cleanValue = value.replace(/\./g, '').replace(',', '.');
    const novoPrecoExibicao = parseFloat(cleanValue) || 0;
    
    setPrecoUnitario(novoPrecoExibicao);
    setPrecoUnitarioInterno(novoPrecoExibicao);
    
    // Se temos um total da nota definido, calcular a quantidade exata
    if (totalNotaOriginal > 0 && novoPrecoExibicao > 0) {
      const novaQuantidadeExata = calcularQuantidadeExata(novoPrecoExibicao, totalNotaOriginal);
      const novaQuantidadeExibicao = formatarParaExibicao(novaQuantidadeExata, casasDecimais);
      
      setQuantidade(novaQuantidadeExibicao);
      
      // Atualizar o campo de quantidade no formulário
      formRef.current?.setFieldValue('qtde', novaQuantidadeExibicao);
      
      // O total calculado sempre será igual ao total da nota
      setTotalCalculado(totalNotaOriginal);
      formRef.current?.setFieldValue('subtotal', totalNotaOriginal);
      
      // Limpar erro de validação
      setErroValidacao('');
    } else {
      // Para novo item, recalcular total normalmente
      const novoTotal = calcularTotalExato(quantidade, novoPrecoExibicao);
      const novoTotalFormatado = formatarParaExibicao(novoTotal, casasDecimais);
      setTotalCalculado(novoTotalFormatado);
      formRef.current?.setFieldValue('subtotal', novoTotalFormatado);
      
      if (totalNotaOriginal > 0) {
        validarTotal(novoTotalFormatado, totalNotaOriginal);
      }
    }
  };

  // Handler para mudança na unidade
  const handleUnidadeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const novaUnidade = event.target.value;
    setun(novaUnidade);
    
    if (!novaUnidade || novaUnidade.trim() === '') {
      setErroUnidade('Unidade é obrigatória');
    } else {
      setErroUnidade('');
    }
  };

  useEffect(() => {
    if (props.id === 'novo') {
      // Reset para novo item
      setIdprodd('');
      setdescricao('');
      setValue('');
      setId('');
      setQuantidade(0);
      setPrecoUnitario(0);
      setPrecoUnitarioInterno(0);
      setTotalCalculado(0);
      setTotalNotaOriginal(0);
      setErroValidacao('');
      setErroUnidade('');
      setCasasDecimais(2);
      formRef.current?.setData(itensnovo);
    } else {
      if (props.dados && typeof props.dados === 'object') {
        // Armazenar os valores originais
        const quantidadeOriginal = props.dados.qtde || 0;
        const precoOriginal = props.dados.preco || 0;
        const totalOriginal = props.dados.subtotal || 0;
        const unidadeOriginal = props.dados.un || '';
        
        // Detectar casas decimais do total original
        const casasDetectadas = detectarCasasDecimais(totalOriginal);
        setCasasDecimais(casasDetectadas);
        
        setIdprodd(props.dados.idprodutos);
        setdescricao(props.dados.descricao);
        setTotalNotaOriginal(totalOriginal);
        setun(unidadeOriginal);
        
        // Validar unidade
        if (!unidadeOriginal || unidadeOriginal.trim() === '') {
          setErroUnidade('Unidade é obrigatória');
        } else {
          setErroUnidade('');
        }
        
        // Usar o preço unitário exato (sem arredondamento) internamente
        const precoExato = quantidadeOriginal > 0 ? totalOriginal / quantidadeOriginal : precoOriginal;
        const precoExibicao = formatarParaExibicao(precoExato, casasDetectadas);
        
        setQuantidade(quantidadeOriginal);
        setPrecoUnitarioInterno(precoExato);
        setPrecoUnitario(precoExibicao);
        setTotalCalculado(totalOriginal);
        
        formRef.current?.setData({
          ...props.dados,
          vprod: props,
          vuncom: formatMoneyBR(precoExibicao, casasDetectadas),
          qcom: quantidadeOriginal,
          filtro: 'descricao',
          subtotal: totalOriginal,
          qtde: quantidadeOriginal,
          preco: precoExibicao,
          un: unidadeOriginal,
        });
        
        // Garantir que os campos estejam com os valores corretos
        formRef.current?.setFieldValue('subtotal', totalOriginal);
        formRef.current?.setFieldValue('qtde', quantidadeOriginal);
        formRef.current?.setFieldValue('preco', precoExibicao);
        formRef.current?.setFieldValue('un', unidadeOriginal);
        
        setfiltro('descricao');
      }
    }
  }, [id, formRef, open, props.id, props.dados]);

  const handleProduto = (valor: string | undefined) => {
    if (valor !== undefined) {
      setdescricao(valor);
    }
  };

  const handlemarca = (valor: string | undefined) => {
    if (valor !== undefined) {
      formRef.current?.setFieldValue('marca', valor);
      setmarca(valor);
    }
  };

  const handleun = (valor: string | undefined) => {
    if (valor !== undefined) {
      formRef.current?.setFieldValue('un', valor);
      setun(valor);
      setErroUnidade('');
    }
  };

  const handlereferencia = (valor: string | undefined) => {
    formRef.current?.setFieldValue('referencia', valor);
    setreferencia(valor);
  };

  const handlidprod = (valor: string | undefined) => {
    setIdprodd(valor ?? "");
  };

  const handleSave = (dados: IFormData) => {
    // Validar se a unidade foi preenchida
    if (!un || un.trim() === '') {
      setErroUnidade('Unidade é obrigatória. Selecione uma unidade para o produto.');
      return;
    }
    
    // Para edição, sempre garantir que quantidade × preço = total da nota
    if (totalNotaOriginal > 0) {
      // Usar o preço unitário interno para cálculo preciso
      const totalVerificacao = calcularTotalExato(quantidade, precoUnitarioInterno);
      const tolerancia = casasDecimais === 4 ? 0.0001 : 0.0099;
      
      if (Math.abs(totalVerificacao - totalNotaOriginal) > tolerancia) {
        setErroValidacao(
          `Inconsistência nos valores: ${quantidade} × ${precoUnitario.toFixed(casasDecimais)} = ${totalVerificacao.toFixed(casasDecimais)} ` +
          `mas o total da nota é R$ ${totalNotaOriginal.toFixed(casasDecimais)}. ` +
          `Use o botão "Ajustar" para corrigir automaticamente.`
        );
        return;
      }
    }
    
    // Para novo item ou item válido, prosseguir com o salvamento
    const DadosValidados = {
      ...dados,
      qtde: quantidade,
      preco: precoUnitario,
      subtotal: totalNotaOriginal > 0 ? totalNotaOriginal : totalCalculado,
      un: un || dados.un || '',
      referencia: dados.referencia ?? '',
      marca: dados.marca ?? '',
    };
  
    formValidationSchema.validate(DadosValidados, { abortEarly: false })
      .then((dadosValidados) => {
        const payload: IFormData = {
          ...dadosValidados,
          iditens: props.dados?.iditens ?? dados.iditens ?? '',
          idprodutos: idprodd ?? '',
          ean: props.dados?.ean,
          descricao: descricao ?? '',
          marca: marca ?? '',
          desconto: 0,
          voutros: 0,
          total: 0,
          qtds: 0,
          referencia: referencia ?? '',
          un: un ?? '',
          filtro: filtro ?? '',
        };
        props.onDados(payload);
        onClose();
      })
      .catch((errors: yup.ValidationError) => {
        const validationErrors: IVFormErrors = {};
        errors.inner.forEach((error) => {
          if (!error.path) return;
          validationErrors[error.path] = error.message;
        });
        formRef.current?.setErrors(validationErrors);
      });
  };

  const handleCancel = () => {
    setIdprodd('');
    setdescricao('');
    setmarca('');
    setreferencia('');
    setValue('');
    setId('');
    setQuantidade(0);
    setPrecoUnitario(0);
    setPrecoUnitarioInterno(0);
    setTotalCalculado(0);
    setTotalNotaOriginal(0);
    setErroValidacao('');
    setErroUnidade('');
    setCasasDecimais(2);
    formRef.current?.setData(itensnovo);
    onClose();
  };

  useEffect(() => {
    if (!open) {
      setValue(valueProp);
    } else {
      setId(valueProp);
    }
  }, [valueProp, open]);

  const handleFiltro = (valor: string | undefined) => {
    if (valor != null) {
      setfiltro(valor);
    }
  };

  const renderProductTitle = () => {
    const productName = props.dados?.descricao || descricao || 'Novo Produto';
    const isEditing = props.id !== 'novo';

    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 48,
            height: 48,
            borderRadius: '12px',
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
            color: 'primary.main',
            border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            flexShrink: 0,
            mt: 0.5,
          }}
        >
          {isEditing ? <EditIcon /> : <AddIcon />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
            {isEditing ? 'Editar Item da NF-e' : 'Adicionar Item à NF-e'}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'text.primary',
                fontWeight: 500,
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'normal', 
                lineHeight: 1.4,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {productName}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {isEditing && (
                <Chip 
                  label="Editando" 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  sx={{ 
                    height: 22,
                    fontSize: '0.7rem',
                    borderWidth: '1px',
                    alignSelf: 'flex-start',
                  }}
                />
              )}
              {totalNotaOriginal > 0 && (
                <Chip 
                  icon={<SyncAltIcon sx={{ fontSize: 14 }} />}
                  label={`Total NF-e: R$ ${totalNotaOriginal.toFixed(casasDecimais)}`} 
                  size="small" 
                  color="info" 
                  variant="outlined"
                  sx={{ 
                    height: 22,
                    fontSize: '0.7rem',
                    fontWeight: 500,
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      {...other}
      fullWidth={false}
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
          maxWidth: '500px',
          width: '100%',
        }
      }}
    >
      <Box
        sx={{
          background: (theme) => theme.palette.background.paper,
          color: 'text.primary',
          position: 'relative',
          pt: 3,
          pb: 2,
          px: 3,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: 'text.secondary',
            backgroundColor: 'action.hover',
            '&:hover': {
              backgroundColor: 'action.selected',
              color: 'text.primary',
            }
          }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        
        {renderProductTitle()}
        
        <Divider 
          sx={{ 
            mt: 2, 
            mb: 1.5,
          }} 
        />
        
        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
          {props.dados?.referencia && (
            <Chip
              icon={<InventoryIcon sx={{ fontSize: 14 }} />}
              label={`Ref: ${props.dados.referencia}`}
              size="small"
              variant="outlined"
              sx={{
                height: 24,
                fontSize: '0.75rem',
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.3),
                color: 'text.secondary',
                mb: 0.5,
              }}
            />
          )}
          {props.dados?.marca && (
            <Chip
              label={props.dados.marca}
              size="small"
              variant="outlined"
              sx={{
                height: 24,
                fontSize: '0.75rem',
                borderColor: (theme) => alpha(theme.palette.secondary.main, 0.3),
                color: 'text.secondary',
                mb: 0.5,
              }}
            />
          )}
          {props.dados?.un && (
            <Chip
              label={`UN: ${props.dados.un}`}
              size="small"
              variant="outlined"
              sx={{
                height: 24,
                fontSize: '0.75rem',
                borderColor: (theme) => alpha(theme.palette.info.main, 0.3),
                color: 'text.secondary',
                mb: 0.5,
              }}
            />
          )}
        </Stack>
      </Box>

      <DialogContent dividers sx={{ p: 3 }}>
        {erroValidacao && (
          <Alert 
            severity="warning" 
            icon={<WarningIcon />}
            sx={{ 
              mb: 2,
              borderRadius: '8px',
              '& .MuiAlert-message': {
                fontSize: '0.875rem',
              }
            }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={ajustarPrecoPelaQuantidade}
                sx={{ fontWeight: 500 }}
              >
                Ajustar
              </Button>
            }
          >
            <AlertTitle sx={{ fontWeight: 600 }}>Valor Total Inconsistente</AlertTitle>
            {erroValidacao}
          </Alert>
        )}
        
        {erroUnidade && (
          <Alert 
            severity="error" 
            icon={<WarningIcon />}
            sx={{ 
              mb: 2,
              borderRadius: '8px',
              '& .MuiAlert-message': {
                fontSize: '0.875rem',
              }
            }}
          >
            <AlertTitle sx={{ fontWeight: 600 }}>Unidade Obrigatória</AlertTitle>
            {erroUnidade}
          </Alert>
        )}
        
        <VForm ref={formRef} onSubmit={handleSave} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <Grid container direction="column" spacing={2}>
            <Grid container item direction="row" spacing={2}>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <BasicSelect
                      name='filtro'
                      label='Filtro'
                      aoClicar={handleFiltro}
                      data={[
                        { nameBD: 'descricao', descricao: 'Descrição' },
                        { nameBD: 'codbarraentrada', descricao: 'Código de Barras Entrada' },
                        { nameBD: 'referencia', descricao: 'Referência' },
                      ]}
                      disabled={props.status === false ? isLoading : true}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <AutoCompleteProdutoIdProduto
                      filtro={filtro}
                      isExternalLoading={props.status === false ? isLoading : true}
                      descricao={handleProduto}
                      marca={handlemarca}
                      idproduto={handlidprod}
                      referencia={handlereferencia}
                      un={handleun}
                    />
                  </Grid>
                  
                  <Grid item xs={6}>
                    <VFieldMoneynumber2nota
                      fullWidth
                      size='small'
                      name='qtde'
                      label='Quantidade'
                      disabled={false}
                      onChange={handleQuantidadeChange}
                      error={!!erroValidacao}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <VTextField
                      fullWidth
                      size='small'
                      name='un'
                      label='UN *'
                      disabled={false}
                      onChange={handleUnidadeChange}
                      error={!!erroUnidade}
                      helperText={erroUnidade || "Selecione a unidade do produto"}
                      required
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <VTextField
                      fullWidth
                      size='small'
                      name='referencia'
                      label='Referência'
                      disabled={true}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <VTextField
                      fullWidth
                      size='small'
                      name='marca'
                      label='Marca'
                      disabled={true}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <VFieldMoneynumber2nota
                      fullWidth
                      size='small'
                      name='preco'
                      label='Valor Unitário'
                      disabled={false}
                      onChange={handlePrecoChange}
                      error={!!erroValidacao}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <VFieldMoneynumber2nota
                      fullWidth
                      size='small'
                      name='subtotal'
                      label='Valor Total'
                      disabled={true}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </VForm>
      </DialogContent>

      <DialogActions sx={{ 
        p: 2.5, 
        pt: 2,
        backgroundColor: 'background.default',
        borderTop: 1,
        borderColor: 'divider'
      }}>
        <Stack 
          direction="row" 
          spacing={1.5} 
          sx={{ 
            width: '100%',
            justifyContent: 'flex-end'
          }}
        >
          <Button
            variant="outlined"
            onClick={handleCancel}
            startIcon={<CancelIcon />}
            sx={{
              px: 2.5,
              py: 0.75,
              borderRadius: '8px',
              borderColor: 'grey.300',
              color: 'grey.700',
              fontWeight: 500,
              textTransform: 'none',
              fontSize: '0.875rem',
              '&:hover': {
                borderColor: 'error.main',
                backgroundColor: (theme) => alpha(theme.palette.error.main, 0.04),
                color: 'error.main',
              }
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={props.status === false ? isLoading : !!erroUnidade}
            onClick={save}
            startIcon={<CheckCircleIcon />}
            sx={{
              px: 2.5,
              py: 0.75,
              borderRadius: '8px',
              backgroundColor: 'primary.main',
              color: 'white',
              fontWeight: 500,
              textTransform: 'none',
              fontSize: '0.875rem',
              '&:hover': {
                backgroundColor: 'primary.dark',
                boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
              },
              '&:disabled': {
                backgroundColor: 'action.disabledBackground',
                color: 'action.disabled',
                boxShadow: 'none',
              }
            }}
          >
            Confirmar
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};